import useClassy from '@core/hooks/useClassy';
import usePlanPageUrl from '@core/hooks/usePlanPageUrl';
import Button from '@ui/Button';

import PlanUpgradeContainer from '../PlanUpgradeContainer';

import changelogImg from './images/changelog-preview.webp';
import customComponentsImg from './images/custom-component-preview.webp';
import customPageImg from './images/custom-page-preview.webp';
import discussionsImg from './images/discussions-preview.webp';
import glossaryImg from './images/glossary-preview.webp';
import guidesImg from './images/guides-preview.webp';
import healthCheckImg from './images/health-check-preview.webp';
import landingPageImg from './images/landing-page-preview.webp';
import recipesImg from './images/recipes-preview.webp';
import reusableContentImg from './images/reusable-content-preview.webp';
import classes from './index.module.scss';

export const featureContentMap = {
  'admin-seat-limit': {
    description: 'Admin seat limit reached, ask the project owner to upgrade to restore access.',
    image: null,
    label: null,
    plan: null,
  },
  'ai-disabled': {
    description: 'Your admin has disabled this feature.',
    image: null,
    label: null,
    plan: null,
  },
  'ask-ai': {
    description: 'See the questions your users are asking and how Ask AI is responding.',
    image: null,
    label: 'Ask AI Analytics',
    plan: 'Ask AI',
  },
  branches: {
    description: 'Collaborate on docs in isolation, then merge changes when ready.',
    image: null,
    label: 'Branches',
    plan: 'Pro',
  },
  changelog: {
    description: 'Keep your users updated on the latest changes.',
    image: changelogImg,
    label: 'Changelog',
    plan: 'Pro',
  },
  'custom-pages': {
    description: 'Write custom pages for your users.',
    image: customPageImg,
    label: 'Custom Pages',
    plan: 'Pro',
  },
  discussions: {
    description: 'A community forum for your users.',
    image: discussionsImg,
    label: 'Discussions',
    plan: 'Pro',
  },
  'error-pages': {
    description: 'Configure custom 404 error pages and redirect rules.',
    image: null,
    label: 'Error Pages',
    plan: 'Pro',
  },
  glossary: {
    description: 'Display term definitions without having to make space in your docs content.',
    image: glossaryImg,
    label: 'Glossary',
    plan: 'Pro',
  },
  guides: {
    description: 'Write non-API reference content with Guides.',
    image: guidesImg,
    label: 'Guides',
    plan: 'Pro',
  },
  home: {
    description: 'Add a customizable landing page.',
    image: landingPageImg,
    label: 'Landing Page',
    plan: 'Pro',
  },
  'health-check': {
    description: 'Automatically (or manually) alert your users when your API experiences an issue.',
    image: healthCheckImg,
    label: 'Health Check',
    plan: 'Pro',
  },
  recipes: {
    description: 'Walk your users through features with code.',
    image: recipesImg,
    label: 'Recipes',
    plan: 'Pro',
  },
  'reusable-content': {
    description: 'Edit Markdown once and update everywhere with reusable content.',
    image: reusableContentImg,
    label: 'Reusable Content',
    plan: 'Pro',
  },
  'custom-components': {
    description: 'Build interactive, branded UI blocks with custom MDX components.',
    image: customComponentsImg,
    label: 'Custom Components',
    plan: 'Pro',
  },
} as const;

export interface PayGateBannerProps {
  /**
   * The feature that is gated. Used to determine the content to display in the overlay.
   */
  feature: keyof typeof featureContentMap;
}

export default function PayGateBanner({ feature }: PayGateBannerProps) {
  const bem = useClassy(classes, 'PayGate');
  const content = featureContentMap[feature];
  const isLabelPlural = ['branches', 'custom-pages', 'discussions', 'error-pages', 'guides', 'recipes'].includes(
    feature,
  );
  const planPageUrl = usePlanPageUrl();
  const label = 'label' in content ? content.label : undefined;
  const isDisabled = !content.plan;

  return (
    <PlanUpgradeContainer
      className={bem('-banner', !!content.image && '-banner_image')}
      kind={isDisabled ? 'disabled' : 'paygate'}
    >
      <div className={bem('-body')}>
        {!!content.image && !!label && <img alt={label} className={bem('-image')} src={content.image} />}
        {!!label && (
          <h3 className={bem('-title')}>
            {content.plan ? `${label} ${isLabelPlural ? 'are' : 'is'} available on the ${content.plan} Plan.` : label}
          </h3>
        )}
        <p className={bem('-description')}>{content.description}</p>
      </div>
      {!!content.plan && (
        <div className={bem('-footer')}>
          <Button fullWidth href={planPageUrl.url} kind="contrast" size="md" target={planPageUrl.target}>
            Upgrade
          </Button>
        </div>
      )}
    </PlanUpgradeContainer>
  );
}
